import React, { useEffect, useMemo, useState } from 'react';
import { getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import { MixBar, Modal, TabStrip, alignEnd, familyLabel, headerEnd, unpaginated } from '../../components/ui';
import type { NcrMetric, UnitMode } from '../../components/ui';
import type { ScenarioResult } from '../../types';
import { count, money, signedMoney } from '../../utils/format';
import { compareFamilyNames } from '../../utils/families';
import { AppTable, Button, StatusTag, createRowSpanGetterByKey } from '@allygpo/ally-ux';

export interface CompareScenario {
  scenario_id: number;
  details: ScenarioResult[];
}

interface CompareModalProps {
  visible: boolean;
  onHide: () => void;
  /** Current first, then the compared scenarios. */
  scenarios: CompareScenario[];
  metric: NcrMetric;
  unitMode: UnitMode;
  ncrLabel: string;
  areaLabel: string;
  getLabel: (scenarioId: number) => string;
  getBadge: (scenarioId: number) => React.ReactNode;
  getColor: (familyName: string) => string;
  /** Invoice unit strength by family name; passed only when units are invoice units (non-oral). */
  strengthOf?: (familyName: string) => string | undefined;
  onExport: () => void;
  exporting: boolean;
}

type Tab = 'summary' | 'detail';

/** Compared scenarios shown at once beside Current; more page sideways so columns stay readable. */
const SCENARIOS_PER_PAGE = 4;

const sum = (rows: readonly ScenarioResult[], pick: (r: ScenarioResult) => number | undefined | null) =>
  rows.reduce((total, r) => total + Number(pick(r) || 0), 0);

/**
 * Compare scenarios against Current. Summary: mix, totals and change vs current per scenario.
 * Product-family detail: every metric at once, one block per family in the fixed order, values only.
 */
const CompareModal: React.FC<CompareModalProps> = ({ visible, onHide, scenarios, metric, unitMode, ncrLabel, areaLabel, getLabel, getBadge, getColor, strengthOf, onExport, exporting }) => {
  const [tab, setTab] = useState<Tab>('summary');
  const metricName = metric === 'medicare' ? ncrLabel : 'Weighted';
  const reimbOf = (r: ScenarioResult) => (metric === 'medicare' ? r.estimated_medicare_reimbursement : r.estimated_pvt_reimbursement);
  const ncrOf = (r: ScenarioResult) => (metric === 'medicare' ? r.total_estimated_ncr : r.total_weighted_ncr);
  const unitsOf = (r: ScenarioResult) => (unitMode === 'category' ? r.category_units : r.invoice_units);
  const unitsName = unitMode === 'category' ? 'category units' : 'invoice units';

  const columns = useMemo(() => scenarios.map((s) => {
    const byFamily = new Map<string, ScenarioResult>();
    s.details.forEach((d) => byFamily.set((d.product_family_name ?? '').trim().toLowerCase(), d));
    const mix = [...s.details]
      .filter((d) => Number(d.future_distribution ?? 0) > 0)
      .sort((a, b) => compareFamilyNames(a.product_family_name ?? '', b.product_family_name ?? ''))
      .map((d) => ({ name: d.product_family_name ?? '', share: Number(d.future_distribution ?? 0), color: getColor(d.product_family_name ?? '') }));
    return {
      id: s.scenario_id,
      byFamily,
      mix,
      reimb: sum(s.details, reimbOf),
      price: sum(s.details, (r) => r.net_price),
      rebate: sum(s.details, (r) => r.total_rebate),
      ncr: sum(s.details, ncrOf),
      units: sum(s.details, unitsOf),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [scenarios, metric, unitMode, getColor]);

  const currentNcr = columns.find((c) => c.id === 0)?.ncr ?? null;

  // No limit on how many are compared: Current stays pinned and the rest page 4 at a time.
  const [colPage, setColPage] = useState(0);
  useEffect(() => setColPage(0), [scenarios]);
  const pinned = columns.filter((c) => c.id === 0);
  const others = columns.filter((c) => c.id !== 0);
  const colPageCount = Math.max(1, Math.ceil(others.length / SCENARIOS_PER_PAGE));
  const page = Math.min(colPage, colPageCount - 1);
  const shown = [...pinned, ...others.slice(page * SCENARIOS_PER_PAGE, (page + 1) * SCENARIOS_PER_PAGE)];

  // Families in at least one compared scenario, in the fixed order. The rest are hidden.
  const families = useMemo(() => {
    const names = new Map<string, string>();
    scenarios.forEach((s) => s.details.forEach((d) => {
      if (Number(d.future_distribution ?? 0) > 0 && d.product_family_name) names.set(d.product_family_name.trim().toLowerCase(), d.product_family_name);
    }));
    return Array.from(names.entries()).map(([key, name]) => ({ key, name })).sort((a, b) => compareFamilyNames(a.name, b.name));
  }, [scenarios]);

  const heading = (id: number) => <span className="inline-flex align-items-center u-gap-2 flex-wrap">{getLabel(id)}{getBadge(id)}</span>;
  type Column = typeof columns[number];

  // Summary: one row per measure, one column per scenario.
  type SummaryRow = { key: string; label: string; strong?: boolean; render: (c: Column) => { node: React.ReactNode; className?: string } };
  const summaryRows: SummaryRow[] = [
    {
      key: 'mix',
      label: `Mix (${unitsName})`,
      render: (c) => ({
        node: (
          <span className="flex flex-column u-gap-1">
            <MixBar segments={c.mix} width="100%" />
            {c.mix.map((m) => (
              <span key={m.name} className="flex align-items-center u-gap-1 text-2xs text-secondary">
                <i className="siq-swatch" style={{ background: m.color }} />
                <span className="flex-1 min-w-0 text-truncate">{familyLabel(m.name)}</span>
                <span>{Math.round(m.share * 100)}%</span>
              </span>
            ))}
          </span>
        ),
      }),
    },
    { key: 'reimb', label: `${metricName} reimb.`, render: (c) => ({ node: money(c.reimb) }) },
    { key: 'price', label: 'Price', render: (c) => ({ node: money(c.price) }) },
    { key: 'rebate', label: 'Rebate', render: (c) => ({ node: money(c.rebate) }) },
    { key: 'ncr', label: `${metricName} NCR`, strong: true, render: (c) => ({ node: money(c.ncr), className: c.ncr < 0 ? 'text-danger' : undefined }) },
    {
      key: 'change',
      label: 'Change vs current',
      strong: true,
      render: (c) => {
        if (c.id === 0 || currentNcr === null) return { node: '—', className: 'siq-dim text-tertiary' };
        const delta = c.ncr - currentNcr;
        return { node: signedMoney(delta), className: delta > 0 ? 'siq-pos text-success' : delta < 0 ? 'text-danger' : undefined };
      },
    },
  ];
  const summaryColumns: ColumnDef<SummaryRow>[] = [
    { id: 'label', size: 150, header: '', meta: { cellClassName: 'font-semibold text-secondary vertical-align-top' }, cell: ({ row }) => row.original.label },
    ...shown.map((c): ColumnDef<SummaryRow> => ({
      id: `s${c.id}`,
      size: 140,
      header: () => headerEnd(heading(c.id)),
      meta: { ...alignEnd, cellClassName: ({ rowData }) => ['vertical-align-top', rowData.strong && 'font-semibold', rowData.render(c).className].filter(Boolean).join(' ') },
      cell: ({ row }) => row.original.render(c).node,
    })),
  ];
  const summaryTable = useReactTable({ ...unpaginated, data: summaryRows, columns: summaryColumns, getRowId: (row) => row.key, getCoreRowModel: getCoreRowModel() });

  // Detail: every measure per family; the family cell spans its measures.
  type Line = { label: string; strong?: boolean; value: (c: Column, row: ScenarioResult | undefined) => { text: string; neg?: boolean } };
  const lines: Line[] = [
    { label: 'Mix share', value: (_c, r) => ({ text: r ? `${Math.round(Number(r.future_distribution ?? 0) * 100)}%` : '—' }) },
    { label: unitMode === 'category' ? 'Category units' : 'Invoice units', value: (_c, r) => ({ text: r ? count(unitsOf(r)) : '—' }) },
    { label: `${metricName} reimb.`, value: (_c, r) => ({ text: r ? money(reimbOf(r)) : '—' }) },
    { label: 'Price', value: (_c, r) => ({ text: r ? money(r.net_price ?? 0) : '—' }) },
    { label: 'Rebate', value: (_c, r) => ({ text: r ? money(r.total_rebate) : '—' }) },
    { label: `${metricName} NCR`, strong: true, value: (_c, r) => { const v = r ? ncrOf(r) : undefined; return { text: r ? money(v) : '—', neg: v != null && v < 0 }; } },
  ];
  const totalLines: Line[] = [
    { label: 'Mix share', value: () => ({ text: '100%' }) },
    { label: lines[1].label, value: (c) => ({ text: count(c.units) }) },
    { label: lines[2].label, value: (c) => ({ text: money(c.reimb) }) },
    { label: 'Price', value: (c) => ({ text: money(c.price) }) },
    { label: 'Rebate', value: (c) => ({ text: money(c.rebate) }) },
    { label: lines[5].label, strong: true, value: (c) => ({ text: money(c.ncr), neg: c.ncr < 0 }) },
  ];
  type DetailRow = { group: string; title: React.ReactNode; line: Line; rowFor: (c: Column) => ScenarioResult | undefined; total?: boolean };
  const detailRows: DetailRow[] = [
    ...families.flatMap((f) => {
      const title = (
        <span className="flex align-items-start u-gap-2">
          <i className="siq-swatch u-mt-1" style={{ background: getColor(f.name) }} />
          <span>
            {familyLabel(f.name)}
            {strengthOf?.(f.name) && <span className="siq-family-strength text-tertiary text-2xs font-medium white-space-nowrap block">{strengthOf(f.name)}</span>}
          </span>
        </span>
      );
      const rowFor = (c: Column) => {
        const row = c.byFamily.get(f.key);
        return row && Number(row.future_distribution ?? 0) > 0 ? row : undefined;
      };
      return lines.map((line) => ({ group: f.key, title, line, rowFor }));
    }),
    ...totalLines.map((line) => ({ group: '__total', title: 'Total', line, rowFor: () => undefined, total: true })),
  ];
  const detailColumns: ColumnDef<DetailRow>[] = [
    {
      id: 'family',
      size: 130,
      header: () => <span>Product family{strengthOf && <span className="block text-2xs text-tertiary">Invoice unit strength</span>}</span>,
      meta: { getRowSpan: createRowSpanGetterByKey<DetailRow>((row) => row.group), cellClassName: 'font-semibold vertical-align-top' },
      cell: ({ row }) => row.original.title,
    },
    { id: 'metric', size: 120, header: 'Metric', meta: { cellClassName: 'text-secondary' }, cell: ({ row }) => row.original.line.label },
    ...shown.map((c): ColumnDef<DetailRow> => ({
      id: `s${c.id}`,
      size: 110,
      header: () => headerEnd(heading(c.id)),
      meta: {
        ...alignEnd,
        cellClassName: ({ rowData }) => {
          const { text, neg } = rowData.line.value(c, rowData.rowFor(c));
          return ['text-nowrap', rowData.line.strong && 'font-semibold', neg && 'text-danger', text === '—' && 'siq-dim text-tertiary'].filter(Boolean).join(' ');
        },
      },
      cell: ({ row }) => row.original.line.value(c, row.original.rowFor(c)).text,
    })),
  ];
  const detailTable = useReactTable({ ...unpaginated, data: detailRows, columns: detailColumns, getRowId: (row) => `${row.group}:${row.line.label}`, getCoreRowModel: getCoreRowModel() });

  const summary = <AppTable isBordered={false} size="sm" tableLayout="fixed" ariaLabel="Comparison summary" table={summaryTable} />;

  const detail = (
    <div className="flex flex-column u-gap-3">
      <span className="text-xs text-secondary">Every metric per product family · families not in any compared scenario are hidden</span>
      <AppTable
        size="sm"
        tableLayout="auto"
        ariaLabel="Product-family detail"
        table={detailTable}
        rowClassName={({ rowData }) => (rowData.total ? 'bg-surface-container' : '')}
      />
    </div>
  );

  return (
    <Modal
      visible={visible}
      onHide={onHide}
      title="Compare scenarios"
      subtitle={`${areaLabel} · ${metricName} NCR · ${unitsName} · compared against current`}
      width="56.875rem"
      dismissableMask
      className="compare-modal"
      height="80vh"
      beforeBody={
        <div className="flex align-items-center justify-content-between u-gap-4">
          <TabStrip
            tabs={[{ key: 'summary', header: 'Summary' }, { key: 'detail', header: 'Product-family detail' }]}
            active={tab}
            onChange={(key) => setTab(key === 'detail' ? 'detail' : 'summary')}
          />
          {colPageCount > 1 && (
            <nav className="flex align-items-center u-gap-2 text-xs text-secondary text-nowrap" aria-label="Compared scenarios">
              <span className="u-mr-2">Scenarios {page * SCENARIOS_PER_PAGE + 1}–{Math.min(others.length, (page + 1) * SCENARIOS_PER_PAGE)} of {others.length}{pinned.length ? ' · Current stays pinned' : ''}</span>
              <Button variant="primary-outline" onClick={() => setColPage(page - 1)} disabled={page === 0} aria-label="Previous scenarios"><i className="pi pi-angle-left" aria-hidden="true" /></Button>
              <Button variant="primary-outline" onClick={() => setColPage(page + 1)} disabled={page >= colPageCount - 1} aria-label="Next scenarios"><i className="pi pi-angle-right" aria-hidden="true" /></Button>
            </nav>
          )}
        </div>
      }
      footer={<>
        <Button variant="primary-outline" onClick={onHide}>Close</Button>
        <Button onClick={onExport} disabled={exporting}><i className="pi pi-download" aria-hidden="true" />Export comparison</Button>
      </>}
      onExited={() => { setTab('summary'); setColPage(0); }}
    >
      {tab === 'summary' ? summary : detail}
    </Modal>
  );
};

export const scenarioBadge = (scenarioId: number, proposedId: number): React.ReactNode =>
  scenarioId === 0 ? <StatusTag variant="outline">Baseline</StatusTag> : scenarioId === proposedId ? <StatusTag variant="info">Proposed</StatusTag> : null;

export default CompareModal;
